import { categoryEstimationConfig } from "sj-ui-toolkit/src/atlas/utils/kaConstants";
import type { ChoreList, KnowledgeAssessment } from "sj-ui-toolkit/src/client/svcChores";

type ChoreItem = ChoreList["items"][number];

// Chore item whose content is the knowledge-assessment arm of the proto oneof
// (mirrors LessonChoreItem in sj-ui-toolkit plaHelpers).
export type AssessmentChoreItem = ChoreItem & {
  content: { knowledgeAssessment: KnowledgeAssessment };
};

export function isAssessmentChoreItem(item: ChoreItem): item is AssessmentChoreItem {
  return "knowledgeAssessment" in item.content;
}

export interface FormattedAssessment {
  id: string;
  type: "Assessment";
  status: string;
  title: string;
  description: string;
  dueDate?: string;
  points: number;
  questions: number;
  estimatedTime: string;
  locked: boolean;
  assignmentUUID: string;
}

function calculateDueDate(dates: AssessmentChoreItem["schedule"]["dates"]): string | undefined {
  if (!dates || dates.dueAfter?.days == null) return undefined;

  const startDate = new Date(dates.availableAt);
  startDate.setDate(startDate.getDate() + dates.dueAfter.days - 1);

  return startDate.toISOString();
}

export function generateEstimatedTime(sections: string[]): string {
  const estimatedTime: [number, number] = [0, 0]; // [min, max] in minutes
  sections.forEach(section => {
    const sectionEstimation = categoryEstimationConfig[section];
    if (sectionEstimation) {
      const parts = sectionEstimation.estFinishTime.split(" - ");
      const min = parseInt(parts[0]?.trim() ?? "0", 10);
      const max = parseInt(parts[1]?.trim() ?? "0", 10);
      if (!isNaN(min)) estimatedTime[0] += min;
      if (!isNaN(max)) estimatedTime[1] += max;
    }
  });
  return `${estimatedTime[0]} - ${estimatedTime[1]}`;
}

// SHRPA-2566: This endpoint will change before release, we need to update the hardcoded values when they're ready
export function formatAssessmentData(
  filteredAssessments: AssessmentChoreItem[]
): FormattedAssessment[] {
  return filteredAssessments
    .slice()
    .sort((a, b) => {
      const isAInProgress = a.content?.knowledgeAssessment?.status?.progress === "InProgress";
      const isBInProgress = b.content?.knowledgeAssessment?.status?.progress === "InProgress";
      return Number(isBInProgress) - Number(isAInProgress);
    })
    .map(assessment => {
      // calculate estimated time based on each category/section (e.g. topicPrefix)
      const estimatedTime = generateEstimatedTime(
        assessment.content.knowledgeAssessment.configuration?.filters?.sections ?? []
      );

      return {
        // assignmentTaskUuid is generated as `unknown` in the proto types; it is a uuid string
        id: String(assessment.assignmentTaskUuid ?? ""),
        type: "Assessment",
        status: assessment.content.knowledgeAssessment.status.progress,
        title: assessment.listing.title,
        description: assessment.listing.description,
        dueDate: calculateDueDate(assessment.schedule.dates),
        points: 200, // TODO: need this data from API
        questions: 20, // TODO: need this data from API
        estimatedTime: estimatedTime,
        locked: false, // Unclear if we need this anymore with assignmentTaskUuid
        assignmentUUID: assessment.assignment.uuid,
      };
    });
}
